import { EmeraldIcon } from "@emerald-react/icon";
import { useGetEdpTransformedImageQuery } from "@services/media";
import { type TransformedImageProps } from "@shared/types/transformedImage.types";
import classNames from "classnames";
import { useEffect } from "react";

/**
 * Custom hook for transforming EDP image URLs using RTK Query
 */
export const useTransformedImageUrl = (imageUrl?: string) => {
  // Only fetch transformed image if URL contains s3.amazonaws.com
  const shouldTransform =
    imageUrl?.includes("https://s3.amazonaws.com") || false;

  const {
    data: blob,
    isError,
    isFetching,
    error,
  } = useGetEdpTransformedImageQuery(
    { imageUrl: imageUrl || "" },
    {
      skip: !imageUrl || !shouldTransform,
    },
  );

  // Create blob URL directly from the blob data
  const blobUrl = blob ? URL.createObjectURL(blob) : null;

  // Cleanup blob URL when component unmounts or blob changes
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  // Return blob URL if transformation was performed and successful, otherwise return original URL
  const transformedImageUrl = shouldTransform ? blobUrl || imageUrl : imageUrl;

  return {
    transformedImageUrl,
    isLoading: shouldTransform ? isFetching : false,
    isError: shouldTransform ? isError : false,
    error: shouldTransform ? error : null,
  };
};

/**
 * A simple image component that handles EDP URL transformation.
 * Supports both regular image and background image modes.
 */
const TransformedImage = ({
  imageUrl,
  alt = "Image",
  className,
  children,
  asBackground = false,
  showFallback = true,
  imgClass,
  ...rest
}: TransformedImageProps) => {
  const { transformedImageUrl, isError } = useTransformedImageUrl(imageUrl);
  // Show fallback if no URL or error
  if (!imageUrl || isError) {
    return (
      <div
        className={classNames(
          "flex items-center justify-center size-full bg-gray-100 text-gray-400",
          className,
        )}
        {...rest}
      >
        {children ||
          (showFallback && (
            <EmeraldIcon icon="image" className="text-2xl opacity-50" />
          ))}
      </div>
    );
  }

  // Background image mode
  if (asBackground) {
    return (
      <div
        className={classNames("bg-center bg-no-repeat", className)}
        style={{
          backgroundImage: transformedImageUrl
            ? `url(${new URL(transformedImageUrl).href})`
            : undefined,
        }}
        {...rest}
      >
        {children}
      </div>
    );
  }

  // Regular image mode
  return (
    <div
      className={classNames("relative flex items-center", className)}
      {...rest}
    >
      <img src={transformedImageUrl} alt={alt} className={imgClass} />
      {children}
    </div>
  );
};

export default TransformedImage;
